import type { TableState, Updater } from '@tanstack/react-table';
import { merge, omit, pick } from 'lodash-es';
import { useState } from 'react';

// State not stored in either localStorage or sessionStorage.
const blacklistProps = ['rowSelection'];

// Properties only preserved in session instead of localStorage.
const sessionOnlyProps = ['pagination'];

function getStorageState(persistId: string, storage: Storage) {
  const data = storage.getItem(persistId);
  return data ? JSON.parse(data) : {};
}

function getLocalStorageState(persistId: string) {
  return getStorageState(persistId, localStorage);
}
function getSessionStorageState(persistId: string) {
  return getStorageState(persistId, sessionStorage);
}

function saveLocalStorageState(
  persistId: string,
  nextState: Partial<TableState>
) {
  const localStorageData = omit(nextState, sessionOnlyProps);
  localStorage.setItem(persistId, JSON.stringify(localStorageData));
}
function saveSessionStorageState(
  persistId: string,
  nextState: Partial<TableState>
) {
  const sessionStorageData = pick(nextState, sessionOnlyProps);
  sessionStorage.setItem(persistId, JSON.stringify(sessionStorageData));
}

function getDefaultState() {
  return { pagination: { pageIndex: 0, pageSize: 50 } };
}

function getPersistId(tableId: string | undefined) {
  if (tableId === undefined) {
    return undefined;
  }
  return ['table', tableId].join('.');
}

const getInitialState = (
  tableId: string | undefined,
  initialState: Partial<TableState>
) => {
  const persistId = getPersistId(tableId);

  if (persistId === undefined) {
    return merge({}, getDefaultState(), initialState);
  }

  return merge(
    {},
    getDefaultState(),
    initialState,
    getLocalStorageState(persistId),
    getSessionStorageState(persistId)
  );
};

const save = (tableId: string | undefined, nextState: TableState) => {
  const persistId = getPersistId(tableId);

  const pruned = omit(nextState, blacklistProps);

  if (typeof persistId === 'string') {
    saveLocalStorageState(persistId, pruned);
    saveSessionStorageState(persistId, pruned);
  }
};

export const useTableState = (
  tableId: string | undefined,
  initialState: Partial<TableState> = {}
) => {
  const [tableState, setTableState] = useState<TableState>(
    getInitialState(tableId, initialState)
  );

  const onStateChange = (updater: Updater<TableState>) => {
    const nextState =
      typeof updater === 'function' ? updater(tableState) : updater;
    setTableState(nextState);
    save(tableId, nextState);
  };

  return { tableState, onStateChange };
};
